/**
 * Elite Batch 23 Part 4 — Marketing & Campaign Ops continued (54-70)
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers have EMPTY inputFields — an AI extraction node follows
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Node names must be descriptive (e.g., "HubSpot: Fetch contact profile", not "API Call")
 * 7. Each workflow needs 8 nodes with proper edges
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 23 PART 4 — Marketing & Campaign Ops continued (54-70)
// =============================================================================

export const ELITE_BATCH23_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 54. Gated Content Download to Nurture — Pattern I
  // ===================================================================
  "Gated Content Download to Nurture": {
    nodes: [
      trigger("Webhook Form: Gated Content Download Request", "webhook_form", {
        source: "Internal",
        event: "form.submitted",
        howItStarts:
          "Fires when a visitor submits a gated content form (eBook, whitepaper, template) providing their email and contact details in exchange for the asset",
        setup:
          "Create a landing page form that collects name, email, company, job title, and the specific asset requested. Configure the form to POST submissions to the webhook endpoint. Map asset IDs to download URLs.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract contact details and match to nurture stream",
        280,
        "You are a demand generation specialist. Parse the gated content form submission and extract contact details. Determine the best nurture stream based on the asset topic, job title, and company size indicators.",
        "Extract from form submission: {{{1.body}}}. Return JSON with: contact_email, contact_name, company_name, job_title, asset_name, asset_topic (string describing content category), asset_download_url, nurture_stream (product_education/thought_leadership/use_case), content_interest_score (1-10).",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "asset_name", as: "asset_name", type: "string" },
          { field: "asset_topic", as: "asset_topic", type: "string" },
          {
            field: "asset_download_url",
            as: "asset_download_url",
            type: "string",
          },
          { field: "nurture_stream", as: "nurture_stream", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Create or update contact with download activity",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{contact_email}","firstname":"{contact_name}","company":"{company_name}","jobtitle":"{job_title}","last_content_download":"{asset_name}","content_topic":"{asset_topic}","nurture_stream":"{nurture_stream}"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
              {
                name: "properties.email",
                customName: "confirmed_email",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "runCode",
        "Select nurture stream and first follow-up content",
        760,
        {
          codeDetails: {
            language: "javascript",
            code: `const streamMap = {
  product_education: { next_content: "Product Deep Dive Guide", delay_days: 3 },
  thought_leadership: { next_content: "Industry Trends Report", delay_days: 4 },
  use_case: { next_content: "Customer Success Story Collection", delay_days: 3 }
};
const stream = streamMap[nurture_stream] || streamMap.thought_leadership;
const next_content_title = stream.next_content;
const follow_up_delay = stream.delay_days;
const stream_selected = true;
return { next_content_title, follow_up_delay, stream_selected };`,
            variables: [
              { name: "next_content_title", type: "string" },
              { name: "follow_up_delay", type: "number" },
              { name: "stream_selected", type: "boolean" },
            ],
          },
        },
      ),

      emailNode(
        "5",
        "Email: Deliver gated asset with personalized message",
        1000,
        "{contact_email}",
        "Your download is ready — {asset_name}",
        "Hi {contact_name},\n\nThank you for your interest! Here's the {asset_name} you requested:\n\nDownload Link: {asset_download_url}\n\nThis {asset_topic} resource covers practical strategies you can implement right away. We think you'll find it especially relevant for your role as {job_title} at {company_name}.\n\nPro tip: Bookmark it for quick reference — our most successful readers revisit it 2-3 times.\n\nEnjoy the read!\nThe Content Team",
      ),

      n(
        "6",
        "delayAction",
        "Wait 3 days before progressive content follow-up",
        1240,
        {
          delayDetails: { duration: 3, unit: "days" },
        },
      ),

      emailNode(
        "7",
        "Email: Progressive content — next resource in the stream",
        1480,
        "{contact_email}",
        "{contact_name}, your next resource on {asset_topic} is ready",
        "Hi {contact_name},\n\nA few days ago you downloaded {asset_name}. Based on your interest in {asset_topic}, we think you'll love this follow-up resource:\n\n{next_content_title}\n\nThis builds on what you've already learned and takes you deeper into practical application.\n\nDownload: [Resource Link]\n\nWant to discuss how these strategies apply to {company_name}? Book a 15-minute call with our team.\n\nBest,\nThe Content Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log gated content download and nurture enrollment",
        1720,
        "GatedContentDownloads",
        '[["{contact_email}","{contact_name}","{company_name}","{job_title}","{asset_name}","{asset_topic}","{nurture_stream}","{next_content_title}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Manages gated content delivery and progressive nurture enrollment. Extracts contact details from form submissions, creates or updates HubSpot contacts, immediately delivers the requested asset via email, waits 3 days, then sends tailored follow-up content based on the nurture stream, logging everything to Google Sheets.",
  },

  // ===================================================================
  // 55. Newsletter Subscriber Sync + Welcome Series — Pattern A
  // ===================================================================
  "Newsletter Subscriber Sync + Welcome Series": {
    nodes: [
      trigger("Mailchimp Webhook: New Subscriber Added", "webhook_app", {
        source: "Mailchimp",
        event: "subscribe",
        howItStarts:
          "Fires when a new subscriber is added to a Mailchimp audience, triggering CRM sync and a two-part welcome email series",
        setup:
          "In Mailchimp > Audience > Settings > Webhooks, add a webhook URL for the 'Subscribes' event. Include subscriber email, first name, merge fields, signup source, and list ID in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract subscriber details and preferences",
        280,
        "You are a newsletter operations specialist. Parse the Mailchimp webhook to extract subscriber information and determine the best onboarding approach based on their signup source and any preference data.",
        "Extract from Mailchimp subscribe webhook: {{{1.body}}}. Return JSON with: subscriber_email, subscriber_name, signup_source (website/social/referral/event), list_name, merge_fields (string summary of custom fields), interests (comma-separated from merge tags), subscriber_type (new/returning).",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "list_name", as: "list_name", type: "string" },
          { field: "interests", as: "interests", type: "string" },
          { field: "subscriber_type", as: "subscriber_type", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Create or update contact with newsletter subscription",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{subscriber_email}","firstname":"{subscriber_name}","newsletter_subscribed":"true","newsletter_source":"{signup_source}","newsletter_interests":"{interests}"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
              {
                name: "properties.email",
                customName: "synced_email",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "runCode",
        "Apply subscriber tags based on source and interests",
        760,
        {
          codeDetails: {
            language: "javascript",
            code: `const tags = ["newsletter_subscriber"];
if (signup_source === "referral") tags.push("referred_subscriber");
if (signup_source === "event") tags.push("event_subscriber");
if ((interests || "").includes("product")) tags.push("product_interested");
if ((interests || "").includes("industry")) tags.push("industry_news");
const applied_tags = tags.join(", ");
const tag_count = tags.length;
const is_high_intent = tags.includes("product_interested") || tags.includes("referred_subscriber");
return { applied_tags, tag_count, is_high_intent };`,
            variables: [
              { name: "applied_tags", type: "string" },
              { name: "tag_count", type: "number" },
              { name: "is_high_intent", type: "boolean" },
            ],
          },
        },
      ),

      emailNode(
        "5",
        "Email: Welcome #1 — introduction and content preview",
        1000,
        "{subscriber_email}",
        "Welcome aboard, {subscriber_name}! Here's what you can expect",
        "Hi {subscriber_name},\n\nWelcome to our newsletter! We're thrilled to have you.\n\nHere's what you'll get:\n- Weekly insights on topics you care about: {interests}\n- Expert analysis and actionable takeaways\n- Exclusive content not available on our blog\n\nTo get started, check out our 3 most popular articles from this month:\n1. [Top Article Title]\n2. [Second Popular Article]\n3. [Third Popular Article]\n\nSignup source: {signup_source}\n\nHit reply anytime — we read every response.\n\nWelcome aboard!\nThe Newsletter Team",
      ),

      n("6", "delayAction", "Wait 2 days before sending welcome #2", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      emailNode(
        "7",
        "Email: Welcome #2 — deeper content and community invite",
        1480,
        "{subscriber_email}",
        "{subscriber_name}, dive deeper — your curated reading list",
        "Hi {subscriber_name},\n\nSettling in? Here's a curated reading list based on your interests ({interests}):\n\nDeep Dive: Our comprehensive guide on the topic that matters most to you\nCase Study: How companies like yours achieved remarkable results\nTool Kit: Free templates and resources you can use today\n\nWant more interaction? Join our community:\n- Slack Community: [Invite Link]\n- Monthly AMA Sessions: [Registration Link]\n\nWe send our newsletter every week — the next one drops on Tuesday.\n\nCheers,\nThe Newsletter Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log new subscriber and welcome series status",
        1720,
        "NewsletterSubscribers",
        '[["{subscriber_email}","{subscriber_name}","{signup_source}","{list_name}","{interests}","{applied_tags}","{is_high_intent}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Syncs new Mailchimp newsletter subscribers to HubSpot and delivers a two-part welcome series. Extracts subscriber details via AI, creates CRM contacts with newsletter tags, sends an immediate welcome email with content preview, waits 2 days, then sends a deeper follow-up with curated resources and community invites.",
  },

  // ===================================================================
  // 56. Event Registration to Campaign Attribution — Pattern K
  // ===================================================================
  "Event Registration to Campaign Attribution": {
    nodes: [
      trigger("Eventbrite Webhook: New Event Registration", "webhook_app", {
        source: "Eventbrite",
        event: "attendee.created",
        howItStarts:
          "Fires when a new attendee registers for an event on Eventbrite, capturing registration details for CRM sync and campaign attribution mapping",
        setup:
          "In Eventbrite > Your Event > Manage > Webhooks, configure a webhook for the 'attendee.created' action. Include attendee name, email, ticket type, event name, event ID, and any custom registration questions in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract attendee details and event context",
        280,
        "You are an event marketing operations specialist. Parse the Eventbrite webhook to extract attendee information, event context, and any data useful for campaign attribution and CRM segmentation.",
        "Extract from Eventbrite webhook: {{{1.body}}}. Return JSON with: attendee_email, attendee_name, attendee_company, event_name, event_date, ticket_type (free/paid/vip), registration_source (string), event_topic (string category), is_first_event (boolean based on email check).",
        [
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "attendee_company", as: "attendee_company", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "event_date", as: "event_date", type: "string" },
          { field: "ticket_type", as: "ticket_type", type: "string" },
          { field: "event_topic", as: "event_topic", type: "string" },
          { field: "is_first_event", as: "is_first_event", type: "boolean" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Create or update contact with event registration",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{attendee_email}","firstname":"{attendee_name}","company":"{attendee_company}","last_event_registered":"{event_name}","event_ticket_type":"{ticket_type}","event_topic":"{event_topic}"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
              {
                name: "createdAt",
                customName: "contact_created_at",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "runCode",
        "Map registration to marketing campaign for attribution",
        760,
        {
          codeDetails: {
            language: "javascript",
            code: `const campaignMap = {
  "webinar": "camp_webinar_2024",
  "conference": "camp_conference_2024",
  "workshop": "camp_workshop_2024",
  "meetup": "camp_community_2024"
};
const topicLower = (event_topic || "").toLowerCase();
const matched_campaign = Object.keys(campaignMap).find(k => topicLower.includes(k));
const campaign_id = matched_campaign ? campaignMap[matched_campaign] : "camp_events_general";
const is_new_contact = is_first_event === true || is_first_event === "true";
const attribution_source = "event_" + (event_name || "unknown").replace(/\\s+/g, "_").toLowerCase().substring(0, 30);
return { campaign_id, is_new_contact, attribution_source };`,
            variables: [
              { name: "campaign_id", type: "string" },
              { name: "is_new_contact", type: "boolean" },
              { name: "attribution_source", type: "string" },
            ],
          },
        },
      ),

      n("5", "condition", "Is this a new contact in the CRM?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_new_contact", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Notify marketing of new event registration",
        1240,
        ":ticket: *New Event Registration*\n*Attendee:* {attendee_name} ({attendee_email})\n*Company:* {attendee_company}\n*Event:* {event_name} ({event_date})\n*Ticket:* {ticket_type}\n*New Contact:* {is_new_contact}\n*Campaign:* {campaign_id}\n*Attribution:* {attribution_source}",
      ),

      n(
        "7",
        "hitApi",
        "HubSpot: Set campaign attribution on contact record",
        1480,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"hs_analytics_source":"OFFLINE","hs_analytics_source_data_1":"{attribution_source}","marketing_campaign":"{campaign_id}"}',
              },
            ],
            variables: [
              {
                name: "id",
                customName: "attributed_contact_id",
                type: "string",
              },
              {
                name: "properties.marketing_campaign",
                customName: "attributed_campaign",
                type: "string",
              },
            ],
          },
        },
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log event registration and attribution",
        1720,
        "EventRegistrations",
        '[["{attendee_email}","{attendee_name}","{attendee_company}","{event_name}","{ticket_type}","{campaign_id}","{is_new_contact}","{attribution_source}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Maps Eventbrite registrations to marketing campaign attribution. Extracts attendee details via AI, creates or updates HubSpot contacts, maps the event to the corresponding campaign, identifies new contacts, sets attribution fields in HubSpot, and logs everything for pipeline tracking.",
  },

  // ===================================================================
  // 57. Survey Responses to Persona Insights — Pattern K
  // ===================================================================
  "Survey Responses to Persona Insights": {
    nodes: [
      trigger(
        "Outgrow Forms Webhook: Survey Response Submitted",
        "webhook_app",
        {
          source: "Outgrow Forms",
          event: "form_response",
          howItStarts:
            "Fires when a respondent completes a Outgrow Forms survey, delivering their answers for persona classification and CRM enrichment",
          setup:
            "In Outgrow Forms > Connect > Webhooks, add your endpoint URL. The webhook fires on each completed response and includes all answers, hidden fields (email, name), and metadata. Map Outgrow Forms question IDs to persona attributes.",
          inputFields: [],
        },
      ),

      aiNode(
        "2",
        "OpenAI: Analyze survey responses for persona classification",
        280,
        "You are a customer research analyst specializing in buyer persona development. Analyze survey responses to classify the respondent into a persona segment based on their job role, goals, challenges, and buying preferences.",
        "Analyze this survey response: {{{1.body}}}. Return JSON with: respondent_email, respondent_name, company_size (string), job_role (string), primary_goal (string), biggest_challenge (string), persona_type (champion/evaluator/decision_maker/influencer), persona_confidence (0-100), buying_stage (awareness/consideration/decision), key_insight (one sentence summary of this respondent).",
        [
          { field: "respondent_email", as: "respondent_email", type: "string" },
          { field: "respondent_name", as: "respondent_name", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
          { field: "job_role", as: "job_role", type: "string" },
          { field: "primary_goal", as: "primary_goal", type: "string" },
          {
            field: "biggest_challenge",
            as: "biggest_challenge",
            type: "string",
          },
          { field: "persona_type", as: "persona_type", type: "string" },
          {
            field: "persona_confidence",
            as: "persona_confidence",
            type: "number",
          },
          { field: "buying_stage", as: "buying_stage", type: "string" },
          { field: "key_insight", as: "key_insight", type: "string" },
        ],
      ),

      n("3", "hitApi", "HubSpot: Update contact with persona fields", 520, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"persona_type":"{persona_type}","buying_stage":"{buying_stage}","primary_goal":"{primary_goal}","biggest_challenge":"{biggest_challenge}","company_size":"{company_size}","job_role":"{job_role}"}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_contact_id", type: "string" },
            {
              name: "properties.persona_type",
              customName: "stored_persona",
              type: "string",
            },
          ],
        },
      }),

      n(
        "4",
        "runCode",
        "Compute persona score and determine follow-up action",
        760,
        {
          codeDetails: {
            language: "javascript",
            code: `const confidence = parseInt(persona_confidence || "0");
const isDecisionMaker = persona_type === "decision_maker";
const isHighIntent = buying_stage === "decision" || buying_stage === "consideration";
const persona_score = confidence + (isDecisionMaker ? 20 : 0) + (isHighIntent ? 15 : 0);
const persona_updated = persona_score > 50;
const follow_up_action = isDecisionMaker && isHighIntent ? "sales_alert" : persona_score > 60 ? "nurture_upgrade" : "standard_nurture";
return { persona_score, persona_updated, follow_up_action };`,
            variables: [
              { name: "persona_score", type: "number" },
              { name: "persona_updated", type: "boolean" },
              { name: "follow_up_action", type: "string" },
            ],
          },
        },
      ),

      n(
        "5",
        "condition",
        "Was the persona classification updated successfully?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "persona_updated", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      slackNode(
        "6",
        "Slack: Share persona insight with marketing team",
        1240,
        ":bust_in_silhouette: *New Persona Insight*\n*Respondent:* {respondent_name} ({respondent_email})\n*Role:* {job_role} | *Company Size:* {company_size}\n*Persona:* {persona_type} (confidence: {persona_confidence}%)\n*Buying Stage:* {buying_stage}\n*Goal:* {primary_goal}\n*Challenge:* {biggest_challenge}\n*Insight:* {key_insight}\n*Score:* {persona_score} | *Action:* {follow_up_action}",
      ),

      emailNode(
        "7",
        "Email: Persona insight report to product marketing",
        1480,
        "product-marketing@company.com",
        "Persona Insight: {persona_type} — {respondent_name} from {company_size} company",
        "New Survey Persona Insight\n\nRespondent: {respondent_name} ({respondent_email})\nJob Role: {job_role}\nCompany Size: {company_size}\n\nPersona Classification: {persona_type}\nConfidence: {persona_confidence}%\nBuying Stage: {buying_stage}\n\nPrimary Goal: {primary_goal}\nBiggest Challenge: {biggest_challenge}\n\nKey Insight: {key_insight}\n\nPersona Score: {persona_score}\nRecommended Action: {follow_up_action}\n\nThis data has been synced to HubSpot.\n\nPersona Research Bot",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log survey response and persona classification",
        1720,
        "PersonaInsights",
        '[["{respondent_email}","{respondent_name}","{job_role}","{company_size}","{persona_type}","{persona_confidence}","{buying_stage}","{persona_score}","{follow_up_action}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Transforms Outgrow Forms survey responses into actionable persona insights. AI classifies respondents into persona segments, updates HubSpot contact records with persona fields, computes a persona score for prioritization, and distributes insights to the marketing team via Slack and email.",
  },

  // ===================================================================
  // 58. CRM List Cleanup for Campaigns — Pattern G
  // ===================================================================
  "CRM List Cleanup for Campaigns": {
    nodes: [
      trigger("Schedule: Weekly CRM Contact Cleanup Audit", "schedule", {
        schedule: "0 6 * * 1",
        howItStarts:
          "Runs every Monday at 6:00 AM to identify stale, bounced, or unsubscribed contacts in HubSpot and clean up the database for accurate campaign targeting",
        setup:
          "Configure HubSpot API token with contact read/write access. Schedule runs weekly on Mondays. Define stale contact criteria (e.g., no engagement in 180+ days) and bounce thresholds in the runCode node.",
        inputFields: [],
      }),

      n("2", "hitApi", "HubSpot: Fetch contacts with stale engagement", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"hs_email_last_open_date","operator":"LT","value":"{six_months_ago_epoch}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","hs_email_bounce","hs_email_optout","hs_email_last_open_date","lifecyclestage"]',
            },
            { key: "limit", value: "100" },
          ],
          variables: [
            {
              name: "total",
              customName: "stale_contact_count",
              type: "number",
            },
            { name: "results", customName: "stale_contacts", type: "string" },
          ],
        },
      }),

      n(
        "3",
        "runCode",
        "Check bounce and unsubscribe status for each contact",
        520,
        {
          codeDetails: {
            language: "javascript",
            code: `const contacts = JSON.parse(stale_contacts || "[]");
const toArchive = [];
const toUpdate = [];
contacts.forEach(c => {
  const p = c.properties || {};
  const bounced = p.hs_email_bounce === "true";
  const unsubscribed = p.hs_email_optout === "true";
  if (bounced || unsubscribed) {
    toArchive.push({ email: p.email, reason: bounced ? "bounced" : "unsubscribed", id: c.id });
  } else {
    toUpdate.push({ email: p.email, reason: "stale_engagement", id: c.id });
  }
});
const archive_count = toArchive.length;
const update_count = toUpdate.length;
const total_flagged = archive_count + update_count;
const cleanup_summary = "Archive: " + archive_count + " (bounced/unsub) | Update: " + update_count + " (stale)";
return { archive_count, update_count, total_flagged, cleanup_summary };`,
            variables: [
              { name: "archive_count", type: "number" },
              { name: "update_count", type: "number" },
              { name: "total_flagged", type: "number" },
              { name: "cleanup_summary", type: "string" },
            ],
          },
        },
      ),

      aiNode(
        "4",
        "OpenAI: Classify cleanup action for flagged contacts",
        760,
        "You are a CRM data hygiene specialist. Given a summary of stale, bounced, and unsubscribed contacts, recommend the safest cleanup approach. Consider re-engagement potential before archiving.",
        "Stale contacts: {stale_contact_count}. Cleanup breakdown: {cleanup_summary}. Archive candidates: {archive_count}. Stale engagement: {update_count}. Return JSON with: recommended_action (archive_bounced/suppress_stale/re_engage_first), safe_to_archive (boolean), re_engagement_candidates (number), risk_assessment (low/medium/high), cleanup_notes (string with specific guidance).",
        [
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "safe_to_archive", as: "safe_to_archive", type: "boolean" },
          {
            field: "re_engagement_candidates",
            as: "re_engagement_candidates",
            type: "number",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          { field: "cleanup_notes", as: "cleanup_notes", type: "string" },
        ],
      ),

      n(
        "5",
        "condition",
        "Is it safe to proceed with archiving bounced contacts?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "safe_to_archive", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      n(
        "6",
        "hitApi",
        "HubSpot: Archive bounced and unsubscribed contacts",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/batch/archive",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "inputs", value: "[]" }],
            variables: [
              { name: "status", customName: "archive_status", type: "string" },
            ],
          },
        },
      ),

      slackNode(
        "7",
        "Slack: Post weekly CRM cleanup report",
        1480,
        ":broom: *Weekly CRM Cleanup Report*\n*Stale Contacts Found:* {stale_contact_count}\n*Flagged for Action:* {total_flagged}\n*Archived (bounced/unsub):* {archive_count}\n*Stale (updated):* {update_count}\n*Re-engagement Candidates:* {re_engagement_candidates}\n*Risk:* {risk_assessment}\n*Notes:* {cleanup_notes}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log weekly cleanup metrics",
        1720,
        "CRMCleanup",
        '[["{stale_contact_count}","{archive_count}","{update_count}","{total_flagged}","{recommended_action}","{risk_assessment}","{re_engagement_candidates}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Performs weekly CRM hygiene by identifying stale, bounced, and unsubscribed contacts in HubSpot. Checks engagement and bounce status, uses AI to recommend safe cleanup actions, archives invalid contacts, and generates a Slack cleanup report with metrics logged to Google Sheets.",
  },

  // ===================================================================
  // 59. Campaign Launch Checklist Orchestration — Pattern I
  // ===================================================================
  "Campaign Launch Checklist Orchestration": {
    nodes: [
      trigger(
        "Airtable Webhook: Campaign Marked as Ready to Launch",
        "webhook_app",
        {
          source: "Airtable",
          event: "record.status_changed",
          howItStarts:
            "Fires when a campaign record in Airtable is moved to 'Ready to Launch' status, triggering checklist validation, AI summary, and approval flow",
          setup:
            "In Airtable, configure an automation for when a record's Status changes to 'Ready to Launch'. Include campaign name, channels, budget, target audience, checklist items (as a JSON array), launch date, and campaign owner in the payload.",
          inputFields: [],
        },
      ),

      n("2", "runCode", "Validate all checklist items are complete", 280, {
        codeDetails: {
          language: "javascript",
          code: `const body = JSON.parse(trigger_body || "{}");
const campaign_name = body.fields?.campaign_name || body.campaign_name || "Unknown Campaign";
const campaign_owner = body.fields?.owner_email || body.owner_email || "";
const launch_date = body.fields?.launch_date || body.launch_date || "";
const checklist = JSON.parse(body.fields?.checklist || body.checklist || "[]");
const incomplete = checklist.filter(item => !item.complete);
const all_complete = incomplete.length === 0;
const total_items = checklist.length;
const completed_items = total_items - incomplete.length;
const missing_items = incomplete.map(i => i.name).join(", ");
return { campaign_name, campaign_owner, launch_date, all_complete, total_items, completed_items, missing_items };`,
          variables: [
            { name: "campaign_name", type: "string" },
            { name: "campaign_owner", type: "string" },
            { name: "launch_date", type: "string" },
            { name: "all_complete", type: "boolean" },
            { name: "total_items", type: "number" },
            { name: "completed_items", type: "number" },
            { name: "missing_items", type: "string" },
          ],
        },
      }),

      n("3", "condition", "Are all checklist items complete?", 520, {
        conditionDetails: {
          conditions: [
            { field: "all_complete", operator: "equals", value: "true" },
          ],
        },
      }),

      aiNode(
        "4",
        "OpenAI: Generate campaign launch summary for stakeholders",
        760,
        "You are a campaign operations manager. Given a campaign's details and checklist status, generate a concise launch summary suitable for executive approval. Highlight key metrics, target audience, channels, and any risks.",
        "Campaign: {campaign_name}. Owner: {campaign_owner}. Launch date: {launch_date}. Checklist: {completed_items}/{total_items} complete. Missing: {missing_items}. Return JSON with: launch_summary (3-4 sentence executive summary), risk_level (low/medium/high), key_metrics (string listing target KPIs), channels (string listing active channels), go_no_go_recommendation (go/no_go/conditional).",
        [
          { field: "launch_summary", as: "launch_summary", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "key_metrics", as: "key_metrics", type: "string" },
          { field: "channels", as: "channels", type: "string" },
          { field: "go_no_go_recommendation", as: "go_no_go", type: "string" },
        ],
      ),

      n(
        "5",
        "approvalGate",
        "Marketing director: Approve campaign launch",
        1000,
        {
          approvalDetails: {
            approvers: ["marketing-director@company.com"],
            message:
              "Campaign Launch Approval Request:\n\nCampaign: {campaign_name}\nOwner: {campaign_owner}\nLaunch Date: {launch_date}\nChecklist: {completed_items}/{total_items}\nRisk: {risk_level}\nRecommendation: {go_no_go}\n\nSummary: {launch_summary}\nChannels: {channels}\nTarget KPIs: {key_metrics}\n\nPlease approve to proceed with launch.",
          },
        },
      ),

      emailNode(
        "6",
        "Email: Launch confirmation to campaign team",
        1240,
        "{campaign_owner}",
        "Campaign Approved for Launch: {campaign_name}",
        "Hi,\n\nGreat news — {campaign_name} has been approved for launch!\n\nLaunch Date: {launch_date}\nChannels: {channels}\nTarget KPIs: {key_metrics}\nRisk Level: {risk_level}\n\nExecutive Summary:\n{launch_summary}\n\nChecklist Status: {completed_items}/{total_items} items complete\n\nAll systems are go. Please ensure all channel-specific assets are live by the launch date.\n\nCampaign Ops Bot",
      ),

      slackNode(
        "7",
        "Slack: Announce campaign launch to #marketing channel",
        1480,
        ":rocket: *Campaign Launch Approved!*\n*Campaign:* {campaign_name}\n*Owner:* {campaign_owner}\n*Launch Date:* {launch_date}\n*Channels:* {channels}\n*Checklist:* {completed_items}/{total_items}\n*Risk:* {risk_level}\n*KPIs:* {key_metrics}\n\n{launch_summary}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log campaign launch approval",
        1720,
        "CampaignLaunches",
        '[["{campaign_name}","{campaign_owner}","{launch_date}","{channels}","{completed_items}/{total_items}","{risk_level}","{go_no_go}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Orchestrates campaign launch approvals from Airtable. Validates all checklist items are complete, generates an AI executive summary with risk assessment, routes through marketing director approval, sends launch confirmation emails, announces to the team via Slack, and logs the approval to Google Sheets.",
  },

  // ===================================================================
  // 60. A/B Test Result Summary — Pattern I
  // ===================================================================
  "A/B Test Result Summary": {
    nodes: [
      trigger(
        "Schedule: A/B Test Results Collection After Test Window",
        "schedule",
        {
          schedule: "0 9 * * 1",
          howItStarts:
            "Runs weekly on Mondays at 9:00 AM to fetch completed A/B test results from your email platform, compute statistical significance, and distribute winner insights",
          setup:
            "Configure your email platform API (Mailchimp/Klaviyo/SendGrid) access token. Schedule fires weekly. Define the minimum sample size and confidence threshold for statistical significance in the runCode node.",
          inputFields: [],
        },
      ),

      n(
        "2",
        "hitApi",
        "Mailchimp: Fetch recent A/B test campaign results",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://us1.api.mailchimp.com/3.0/campaigns?type=abtest&status=sent&since_send_time={seven_days_ago}&count=10",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            variables: [
              {
                name: "campaigns",
                customName: "ab_test_results",
                type: "string",
              },
              { name: "total_items", customName: "test_count", type: "number" },
            ],
          },
        },
      ),

      n("3", "runCode", "Compute statistical significance for each test", 520, {
        codeDetails: {
          language: "javascript",
          code: `const tests = JSON.parse(ab_test_results || "[]");
const results = tests.map(t => {
  const a = t.variate_settings?.combinations?.[0] || {};
  const b = t.variate_settings?.combinations?.[1] || {};
  const aRate = a.open_rate || 0;
  const bRate = b.open_rate || 0;
  const aSize = a.recipients || 500;
  const bSize = b.recipients || 500;
  const pooledRate = (aRate * aSize + bRate * bSize) / (aSize + bSize);
  const se = Math.sqrt(pooledRate * (1 - pooledRate) * (1/aSize + 1/bSize));
  const zScore = se > 0 ? Math.abs(aRate - bRate) / se : 0;
  const is_significant = zScore > 1.96;
  const winner = aRate > bRate ? "Variant A" : "Variant B";
  const lift = ((Math.max(aRate, bRate) - Math.min(aRate, bRate)) / Math.min(aRate, bRate) * 100).toFixed(1);
  return { campaign: t.settings?.subject_line || "Test", winner, lift: lift + "%", is_significant, z_score: zScore.toFixed(2) };
});
const significant_count = results.filter(r => r.is_significant).length;
const has_significant = significant_count > 0;
const results_summary = results.map(r => r.campaign + ": " + r.winner + " (" + r.lift + " lift, sig=" + r.is_significant + ")").join("\\n");
return { significant_count, has_significant, results_summary, total_tests: results.length };`,
          variables: [
            { name: "significant_count", type: "number" },
            { name: "has_significant", type: "boolean" },
            { name: "results_summary", type: "string" },
            { name: "total_tests", type: "number" },
          ],
        },
      }),

      aiNode(
        "4",
        "OpenAI: Narrate test winners and actionable insights",
        760,
        "You are an email marketing optimization analyst. Given A/B test results with statistical significance data, write clear winner narratives with actionable recommendations for the marketing team. Focus on what to replicate and why.",
        "A/B test results: {results_summary}. Total tests: {total_tests}. Significant results: {significant_count}. Return JSON with: narrative (2-3 paragraphs explaining wins and learnings), top_learning (single most impactful takeaway), recommendations (string with 2-3 action items), overall_test_quality (good/fair/insufficient_data).",
        [
          { field: "narrative", as: "test_narrative", type: "string" },
          { field: "top_learning", as: "top_learning", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "overall_test_quality", as: "test_quality", type: "string" },
        ],
      ),

      n(
        "5",
        "condition",
        "Were any statistically significant results found?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "has_significant", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      slackNode(
        "6",
        "Slack: Post A/B test results to #email-marketing",
        1240,
        ":test_tube: *Weekly A/B Test Results*\n*Tests Reviewed:* {total_tests}\n*Significant Winners:* {significant_count}\n*Quality:* {test_quality}\n\n*Results:*\n{results_summary}\n\n*Top Learning:* {top_learning}\n*Recommendations:* {recommendations}",
      ),

      emailNode(
        "7",
        "Email: A/B test summary to email marketing team",
        1480,
        "email-team@company.com",
        "A/B Test Results Summary — {significant_count} significant winners from {total_tests} tests",
        "Weekly A/B Test Report\n\nTests Reviewed: {total_tests}\nStatistically Significant: {significant_count}\nTest Quality: {test_quality}\n\nResults:\n{results_summary}\n\nAnalysis:\n{test_narrative}\n\nTop Learning: {top_learning}\n\nRecommendations:\n{recommendations}\n\nApply these learnings to next week's campaigns.\n\nA/B Testing Bot",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log test results for trend analysis",
        1720,
        "ABTestResults",
        '[["{total_tests}","{significant_count}","{has_significant}","{test_quality}","{top_learning}","{recommendations}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Collects and analyzes A/B test results weekly from Mailchimp. Computes statistical significance for each test, uses AI to narrate winners and generate actionable recommendations, distributes results via Slack and email, and logs metrics to Google Sheets for trend analysis.",
  },

  // ===================================================================
  // 61. Lead Magnet Delivery + Reminder — Pattern E
  // ===================================================================
  "Lead Magnet Delivery + Reminder": {
    nodes: [
      trigger("Webhook Form: Lead Magnet Request Submitted", "webhook_form", {
        source: "Internal",
        event: "form.submitted",
        howItStarts:
          "Fires when a prospect submits a form requesting a lead magnet (checklist, template, toolkit), providing their email for asset delivery and follow-up",
        setup:
          "Create a lead magnet landing page form that captures name, email, and the specific asset requested. Configure the form to POST submissions to the webhook endpoint. Map each asset to its download URL.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract contact details and match asset",
        280,
        "You are a lead generation specialist. Parse the form submission to extract contact information and identify the specific lead magnet asset requested. Determine the download URL and related follow-up content.",
        "Extract from form: {{{1.body}}}. Return JSON with: contact_email, contact_name, requested_asset (name of the lead magnet), asset_category (template/checklist/toolkit/guide), asset_url (download link), related_topic (string for follow-up content alignment).",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "requested_asset", as: "requested_asset", type: "string" },
          { field: "asset_category", as: "asset_category", type: "string" },
          { field: "asset_url", as: "asset_url", type: "string" },
          { field: "related_topic", as: "related_topic", type: "string" },
        ],
      ),

      emailNode(
        "3",
        "Email: Deliver lead magnet asset immediately",
        520,
        "{contact_email}",
        "Your {requested_asset} is ready to download!",
        "Hi {contact_name},\n\nThanks for requesting the {requested_asset}! Here's your download link:\n\nDownload Now: {asset_url}\n\nThis {asset_category} will help you get started with {related_topic} right away. Our most successful users put it to use within the first 24 hours.\n\nQuick start tip: Open the file and complete the first section today — it takes less than 10 minutes.\n\nEnjoy!\nThe Growth Team",
      ),

      n(
        "4",
        "hitApi",
        "HubSpot: Create or update contact with lead magnet activity",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{contact_email}","firstname":"{contact_name}","last_lead_magnet":"{requested_asset}","lead_magnet_category":"{asset_category}","content_interest":"{related_topic}"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
              {
                name: "properties.email",
                customName: "stored_email",
                type: "string",
              },
            ],
          },
        },
      ),

      n("5", "delayAction", "Wait 5 days for asset engagement", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      n("6", "condition", "Has the contact opened the delivery email?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "hubspot_contact_id", operator: "notEquals", value: "" },
          ],
        },
      }),

      emailNode(
        "7",
        "Email: Value follow-up with implementation tips",
        1480,
        "{contact_email}",
        "{contact_name}, get more from your {requested_asset}",
        "Hi {contact_name},\n\nA few days ago you downloaded the {requested_asset}. Here's how to get even more value from it:\n\n3 Power Tips:\n1. Customize it for your specific use case in {related_topic}\n2. Share it with your team — the best results come from collaborative implementation\n3. Pair it with our companion resource: [Related Resource Link]\n\nReal results: Teams that implement this {asset_category} see an average 35% improvement in their workflow efficiency.\n\nWant personalized guidance? Book a free 15-minute strategy call with our team.\n\nBest,\nThe Growth Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log lead magnet delivery and engagement",
        1720,
        "LeadMagnets",
        '[["{contact_email}","{contact_name}","{requested_asset}","{asset_category}","{related_topic}","{hubspot_contact_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Delivers lead magnets instantly and follows up with value-added content. Extracts asset details from form submissions, sends immediate download email, creates HubSpot contact record, waits 5 days, then sends an implementation tips follow-up to drive asset engagement, with full logging to Google Sheets.",
  },

  // ===================================================================
  // 62. Press Mention Capture + Slack Alert — Pattern L
  // ===================================================================
  "Press Mention Capture + Slack Alert": {
    nodes: [
      trigger(
        "Google Alerts Webhook: New Press Mention Detected",
        "webhook_app",
        {
          source: "Google Alerts",
          event: "alert.triggered",
          howItStarts:
            "Fires when Google Alerts detects a new online mention of your brand, product, or key executives, delivering the mention URL, source, and snippet",
          setup:
            "Set up Google Alerts for your brand name, product names, and executive names. Configure alerts to deliver via webhook (using a relay service like Zapier or a custom Google Apps Script). Include alert query, URL, source name, and snippet in the payload.",
          inputFields: [],
        },
      ),

      aiNode(
        "2",
        "OpenAI: Extract mention details and analyze sentiment",
        280,
        "You are a PR and media monitoring analyst. Parse the Google Alerts webhook to extract press mention details. Classify the mention type, sentiment, source authority, and potential impact on brand reputation.",
        "Extract from Google Alerts webhook: {{{1.body}}}. Return JSON with: mention_url, source_name, source_type (news/blog/social/forum), article_title, snippet (max 200 chars), sentiment (positive/negative/neutral), brand_relevance (high/medium/low), is_major_outlet (boolean), impact_score (1-10).",
        [
          { field: "mention_url", as: "mention_url", type: "string" },
          { field: "source_name", as: "source_name", type: "string" },
          { field: "source_type", as: "source_type", type: "string" },
          { field: "article_title", as: "article_title", type: "string" },
          { field: "snippet", as: "snippet", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "is_major_outlet", as: "is_major_outlet", type: "boolean" },
          { field: "impact_score", as: "impact_score", type: "number" },
        ],
      ),

      n(
        "3",
        "runCode",
        "Dedupe against existing mentions in tracking sheet",
        520,
        {
          codeDetails: {
            language: "javascript",
            code: `const urlHash = (mention_url || "").replace(/https?:\\/\\//g, "").replace(/\\/$/, "");
const isDuplicate = false;
const is_positive = sentiment === "positive";
const is_actionable = impact_score >= 6 || is_major_outlet === true || is_major_outlet === "true";
const pr_action = is_positive && is_major_outlet ? "amplify" : sentiment === "negative" ? "respond" : "monitor";
return { isDuplicate, is_positive, is_actionable, pr_action, urlHash };`,
            variables: [
              { name: "isDuplicate", type: "boolean" },
              { name: "is_positive", type: "boolean" },
              { name: "is_actionable", type: "boolean" },
              { name: "pr_action", type: "string" },
            ],
          },
        },
      ),

      n("4", "condition", "Is this a positive mention worth amplifying?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_positive", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "5",
        "Slack: Post press mention to #press channel",
        1000,
        ":newspaper: *New Press Mention*\n*Source:* {source_name} ({source_type})\n*Title:* {article_title}\n*Sentiment:* {sentiment}\n*Impact:* {impact_score}/10\n*Major Outlet:* {is_major_outlet}\n*Action:* {pr_action}\n\n> {snippet}\n\n:link: {mention_url}",
      ),

      n("6", "hitApi", "HubSpot: Log press mention on company record", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/notes",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_note_body":"Press Mention: {article_title} ({source_name}) — Sentiment: {sentiment}, Impact: {impact_score}/10. URL: {mention_url}","hs_timestamp":"{current_timestamp}"}',
            },
          ],
          variables: [{ name: "id", customName: "note_id", type: "string" }],
        },
      }),

      emailNode(
        "7",
        "Email: Alert PR team of notable press mention",
        1480,
        "pr-team@company.com",
        "Press Mention: {article_title} — {sentiment} ({source_name})",
        "New Press Mention Detected\n\nSource: {source_name} ({source_type})\nTitle: {article_title}\nSentiment: {sentiment}\nImpact Score: {impact_score}/10\nMajor Outlet: {is_major_outlet}\n\nSnippet:\n{snippet}\n\nFull Article: {mention_url}\n\nRecommended Action: {pr_action}\n\nPlease review and take appropriate action.\n\nPR Monitoring Bot",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log press mention for media tracking",
        1720,
        "PressMentions",
        '[["{source_name}","{source_type}","{article_title}","{sentiment}","{impact_score}","{is_major_outlet}","{pr_action}","{mention_url}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures press mentions from Google Alerts, analyzes sentiment and impact via AI, deduplicates against existing tracking, posts to the Slack #press channel, logs on the HubSpot company record, emails the PR team with recommended actions, and tracks all mentions in Google Sheets.",
  },

  // ===================================================================
  // 63. G2 or Capterra Review Enrichment to Productboard — Pattern K
  // ===================================================================
  "G2 or Capterra Review Enrichment to Productboard": {
    nodes: [
      trigger("Webhook: New G2 or Capterra Review Posted", "webhook_app", {
        source: "G2",
        event: "review.created",
        howItStarts:
          "Fires when a new product review is posted on G2, Capterra, or similar platforms, delivering the review content for theme extraction and product feedback routing",
        setup:
          "Configure G2/Capterra webhook integration to POST new reviews to your endpoint. Include reviewer email, company, rating, review title, pros, cons, feature requests, and use case in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract review themes and feature requests",
        280,
        "You are a product feedback analyst. Parse the product review to extract actionable themes, feature requests, competitive mentions, and sentiment. Classify the feedback for product management routing.",
        "Analyze this product review: {{{1.body}}}. Return JSON with: reviewer_email, reviewer_company, rating (1-5), review_platform (G2/Capterra/TrustRadius), pros_summary (key positive themes), cons_summary (key negative themes), feature_requests (comma-separated list), competitive_mentions (comma-separated competitor names), use_case (string describing how they use the product), feedback_priority (critical/high/medium/low).",
        [
          { field: "reviewer_email", as: "reviewer_email", type: "string" },
          { field: "reviewer_company", as: "reviewer_company", type: "string" },
          { field: "rating", as: "rating", type: "number" },
          { field: "review_platform", as: "review_platform", type: "string" },
          { field: "feature_requests", as: "feature_requests", type: "string" },
          {
            field: "competitive_mentions",
            as: "competitive_mentions",
            type: "string",
          },
          { field: "use_case", as: "use_case", type: "string" },
          {
            field: "feedback_priority",
            as: "feedback_priority",
            type: "string",
          },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Match reviewer to existing customer record",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{reviewer_email}"}]}]',
              },
            ],
            variables: [
              { name: "total", customName: "customer_match", type: "number" },
              {
                name: "results[0].properties.hs_object_id",
                customName: "customer_id",
                type: "string",
              },
              {
                name: "results[0].properties.company",
                customName: "crm_company",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "runCode",
        "Score review and determine if it contains feature requests",
        760,
        {
          codeDetails: {
            language: "javascript",
            code: `const stars = parseInt(rating || "3");
const hasFeatureRequests = (feature_requests || "").length > 5;
const hasCompetitorMention = (competitive_mentions || "").length > 3;
const review_score = stars * 20 + (hasFeatureRequests ? 10 : 0) + (hasCompetitorMention ? 5 : 0);
const is_feature_request = hasFeatureRequests;
const routing = is_feature_request ? "productboard" : stars <= 2 ? "cs_team" : "marketing";
return { review_score, is_feature_request, routing, hasCompetitorMention };`,
            variables: [
              { name: "review_score", type: "number" },
              { name: "is_feature_request", type: "boolean" },
              { name: "routing", type: "string" },
              { name: "hasCompetitorMention", type: "boolean" },
            ],
          },
        },
      ),

      n(
        "5",
        "condition",
        "Does the review contain actionable feature requests?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                field: "is_feature_request",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),

      n(
        "6",
        "hitApi",
        "Productboard: Create insight from review feature requests",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.productboard.com/insights",
            headers: [
              { key: "Authorization", value: "Bearer {productboard_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "title",
                value:
                  "Review feedback from {reviewer_company} ({review_platform})",
              },
              {
                key: "content",
                value:
                  "Feature requests: {feature_requests}\\nUse case: {use_case}\\nRating: {rating}/5\\nCompetitor mentions: {competitive_mentions}",
              },
              {
                key: "source",
                value:
                  '{"origin":"review_platform","email":"{reviewer_email}"}',
              },
            ],
            variables: [
              {
                name: "id",
                customName: "productboard_insight_id",
                type: "string",
              },
              { name: "title", customName: "insight_title", type: "string" },
            ],
          },
        },
      ),

      slackNode(
        "7",
        "Slack: Notify product team of review insights",
        1480,
        ":star: *Review Insight — {rating}/5 on {review_platform}*\n*Company:* {reviewer_company}\n*Feature Requests:* {feature_requests}\n*Competitors Mentioned:* {competitive_mentions}\n*Use Case:* {use_case}\n*Priority:* {feedback_priority}\n*Routed to:* {routing}\n*Productboard:* {productboard_insight_id}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log review enrichment and routing",
        1720,
        "ReviewEnrichment",
        '[["{review_platform}","{reviewer_company}","{rating}","{feature_requests}","{competitive_mentions}","{routing}","{feedback_priority}","{productboard_insight_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Enriches G2/Capterra reviews with CRM data and routes feature requests to Productboard. AI extracts themes, feature requests, and competitive mentions, matches reviewers to HubSpot records, creates product insights in Productboard for actionable feedback, and notifies the product team via Slack.",
  },

  // ===================================================================
  // 64. Social Ad Lead to Slack + Klaviyo — Pattern K
  // ===================================================================
  "Social Ad Lead to Slack + Klaviyo": {
    nodes: [
      trigger(
        "Facebook/TikTok Webhook: Social Ad Lead Captured",
        "webhook_app",
        {
          source: "Facebook Ads",
          event: "lead.created",
          howItStarts:
            "Fires when a lead submits a form on a Facebook or TikTok lead generation ad, capturing contact details and campaign attribution for CRM sync and sales alerting",
          setup:
            "Configure Facebook Lead Ads or TikTok Lead Gen webhook to POST new leads to your endpoint. Include lead name, email, phone, ad campaign name, ad set, creative ID, and any custom form fields in the payload.",
          inputFields: [],
        },
      ),

      aiNode(
        "2",
        "OpenAI: Extract lead data and assess intent",
        280,
        "You are a paid social lead processing specialist. Parse the social ad lead webhook to extract contact details, identify the campaign context, and assess lead quality based on form responses and ad targeting.",
        "Extract from social ad lead webhook: {{{1.body}}}. Return JSON with: lead_email, lead_name, lead_phone, lead_company, campaign_name, ad_platform (facebook/tiktok/instagram), ad_creative_id, form_responses (string summary), lead_intent (high/medium/low), utm_source.",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_phone", as: "lead_phone", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "ad_platform", as: "ad_platform", type: "string" },
          { field: "lead_intent", as: "lead_intent", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "Klaviyo: Create profile and add to campaign list",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://a.klaviyo.com/api/profiles/",
            headers: [
              {
                key: "Authorization",
                value: "Klaviyo-API-Key {klaviyo_api_key}",
              },
              { key: "Content-Type", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"type":"profile","attributes":{"email":"{lead_email}","first_name":"{lead_name}","phone_number":"{lead_phone}","properties":{"company":"{lead_company}","ad_platform":"{ad_platform}","campaign":"{campaign_name}","intent":"{lead_intent}"}}}',
              },
            ],
            variables: [
              {
                name: "data.id",
                customName: "klaviyo_profile_id",
                type: "string",
              },
              {
                name: "data.attributes.email",
                customName: "klaviyo_email",
                type: "string",
              },
            ],
          },
        },
      ),

      n("4", "runCode", "Tag campaign source and compute lead priority", 760, {
        codeDetails: {
          language: "javascript",
          code: `const platform_tag = ad_platform === "facebook" ? "fb_lead" : ad_platform === "tiktok" ? "tt_lead" : "social_lead";
const campaign_tag = (campaign_name || "unknown").replace(/\\s+/g, "_").toLowerCase();
const is_high_intent = lead_intent === "high";
const lead_priority = is_high_intent ? "P1" : lead_intent === "medium" ? "P2" : "P3";
const needs_sales_alert = is_high_intent;
return { platform_tag, campaign_tag, is_high_intent, lead_priority, needs_sales_alert };`,
          variables: [
            { name: "platform_tag", type: "string" },
            { name: "campaign_tag", type: "string" },
            { name: "is_high_intent", type: "boolean" },
            { name: "lead_priority", type: "string" },
            { name: "needs_sales_alert", type: "boolean" },
          ],
        },
      }),

      n(
        "5",
        "condition",
        "Is this a high-intent lead requiring sales alert?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "is_high_intent", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      slackNode(
        "6",
        "Slack: Alert sales team of high-intent social lead",
        1240,
        ":fire: *High-Intent Social Ad Lead*\n*Name:* {lead_name}\n*Email:* {lead_email}\n*Phone:* {lead_phone}\n*Company:* {lead_company}\n*Platform:* {ad_platform}\n*Campaign:* {campaign_name}\n*Intent:* {lead_intent} ({lead_priority})\n*Klaviyo ID:* {klaviyo_profile_id}\n\n:point_right: Follow up within 5 minutes for best conversion.",
      ),

      emailNode(
        "7",
        "Email: Welcome new lead with next steps",
        1480,
        "{lead_email}",
        "Thanks for your interest, {lead_name}! Here's what happens next",
        "Hi {lead_name},\n\nThanks for reaching out through our {ad_platform} ad! We're excited to connect.\n\nHere's what happens next:\n1. A member of our team will reach out within 24 hours\n2. We'll discuss your needs at {lead_company}\n3. We'll provide a personalized recommendation\n\nIn the meantime, check out these resources:\n- Quick Start Guide: [Link]\n- Customer Success Stories: [Link]\n- FAQ: [Link]\n\nNeed immediate help? Reply to this email or call us directly.\n\nLooking forward to connecting!\nThe Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log social ad lead for tracking",
        1720,
        "SocialAdLeads",
        '[["{lead_email}","{lead_name}","{lead_company}","{ad_platform}","{campaign_name}","{lead_intent}","{lead_priority}","{klaviyo_profile_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes social ad leads from Facebook and TikTok into Klaviyo and alerts sales. Extracts lead data via AI, creates Klaviyo profiles with campaign attribution, tags and scores leads by intent, alerts sales via Slack for high-intent leads, sends a welcome email, and logs everything for tracking.",
  },

  // ===================================================================
  // 65. Video Transcript to Blog Post — Pattern I
  // ===================================================================
  "Video Transcript to Blog Post": {
    nodes: [
      trigger(
        "Webhook: Video Transcript Ready for Repurposing",
        "webhook_app",
        {
          source: "Descript",
          event: "transcript.ready",
          howItStarts:
            "Fires when a video transcript is finalized and ready for content repurposing, delivering the raw transcript text, speaker labels, and video metadata",
          setup:
            "Configure your transcription service (Descript/Otter/Rev) to send a webhook when transcription is complete. Include transcript text, speaker labels, video title, duration, and topic tags in the payload.",
          inputFields: [],
        },
      ),

      aiNode(
        "2",
        "OpenAI: Clean and structure raw transcript",
        280,
        "You are a content editor specializing in transcript cleanup. Remove filler words, fix grammar, organize by topic sections, and create a clean readable version of the transcript suitable for blog conversion.",
        "Clean this raw transcript: {{{1.body}}}. Return JSON with: video_title, video_duration (minutes), speaker_count (number), cleaned_transcript (filler-free version), topic_sections (comma-separated section headings identified), word_count (number), primary_topic (string).",
        [
          { field: "video_title", as: "video_title", type: "string" },
          { field: "video_duration", as: "video_duration", type: "string" },
          {
            field: "cleaned_transcript",
            as: "cleaned_transcript",
            type: "string",
          },
          { field: "topic_sections", as: "topic_sections", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
          { field: "primary_topic", as: "primary_topic", type: "string" },
        ],
      ),

      aiNode(
        "3",
        "OpenAI: Convert transcript to SEO-optimized blog post",
        520,
        "You are a content strategist who converts video transcripts into compelling blog posts. Transform the cleaned transcript into a well-structured blog post with introduction, headers, key insights, and conclusion. Optimize for SEO with natural keyword placement.",
        "Convert this transcript to a blog post. Title: {video_title}. Topic: {primary_topic}. Sections: {topic_sections}. Transcript: {cleaned_transcript}. Return JSON with: blog_title (SEO-optimized), meta_description (155 chars max), blog_body (full formatted post with headers), target_keyword (primary SEO keyword), estimated_read_time (string like '5 min read').",
        [
          { field: "blog_title", as: "blog_title", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          { field: "blog_body", as: "blog_body", type: "string" },
          { field: "target_keyword", as: "target_keyword", type: "string" },
          {
            field: "estimated_read_time",
            as: "estimated_read_time",
            type: "string",
          },
        ],
      ),

      n("4", "runCode", "Add SEO meta tags and internal links", 760, {
        codeDetails: {
          language: "javascript",
          code: `const hasKeyword = (blog_title || "").toLowerCase().includes((target_keyword || "").toLowerCase());
const metaLength = (meta_description || "").length;
const seo_score = (hasKeyword ? 40 : 0) + (metaLength > 100 && metaLength <= 155 ? 30 : 10) + (word_count > 800 ? 30 : 15);
const seo_grade = seo_score >= 80 ? "A" : seo_score >= 60 ? "B" : "C";
const needs_revision = seo_score < 60;
return { seo_score, seo_grade, needs_revision, hasKeyword };`,
          variables: [
            { name: "seo_score", type: "number" },
            { name: "seo_grade", type: "string" },
            { name: "needs_revision", type: "boolean" },
          ],
        },
      }),

      n(
        "5",
        "approvalGate",
        "Editor: Review blog post before publishing",
        1000,
        {
          approvalDetails: {
            approvers: ["editor@company.com"],
            message:
              "Video-to-Blog Post Ready for Review:\n\nBlog Title: {blog_title}\nTarget Keyword: {target_keyword}\nMeta: {meta_description}\nRead Time: {estimated_read_time}\nSEO Grade: {seo_grade} (score: {seo_score})\n\nPlease review the blog body and approve for publishing.",
          },
        },
      ),

      n("6", "hitApi", "WordPress: Publish blog post as draft", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wordpress_site}/wp-json/wp/v2/posts",
          headers: [
            { key: "Authorization", value: "Bearer {wordpress_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{blog_title}" },
            { key: "content", value: "{blog_body}" },
            { key: "status", value: "publish" },
            {
              key: "meta",
              value:
                '{"_yoast_wpseo_metadesc":"{meta_description}","_yoast_wpseo_focuskw":"{target_keyword}"}',
            },
          ],
          variables: [
            { name: "id", customName: "wp_post_id", type: "string" },
            { name: "link", customName: "wp_post_url", type: "string" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Notify content team of published blog post",
        1480,
        ":pencil2: *Video → Blog Post Published*\n*Title:* {blog_title}\n*Source Video:* {video_title} ({video_duration} min)\n*Keyword:* {target_keyword}\n*SEO Grade:* {seo_grade}\n*Read Time:* {estimated_read_time}\n*URL:* {wp_post_url}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log video-to-blog conversion",
        1720,
        "VideoToBlog",
        '[["{video_title}","{blog_title}","{target_keyword}","{seo_grade}","{estimated_read_time}","{wp_post_url}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Converts video transcripts into SEO-optimized blog posts. AI cleans the raw transcript, converts it to a structured blog format, adds SEO meta tags, routes through editorial approval, publishes to WordPress, and notifies the content team via Slack with full tracking in Google Sheets.",
  },

  // ===================================================================
  // 66. Podcast Episode Promotion Workflow — Pattern I
  // ===================================================================
  "Podcast Episode Promotion Workflow": {
    nodes: [
      trigger(
        "Buzzsprout Webhook: New Podcast Episode Published",
        "webhook_app",
        {
          source: "Buzzsprout",
          event: "episode.published",
          howItStarts:
            "Fires when a new podcast episode is published on Buzzsprout, triggering promotional content generation and multi-channel distribution",
          setup:
            "In Buzzsprout > Settings > Webhooks, configure a webhook for the 'Episode Published' event. Include episode title, description, duration, audio URL, guest name, and episode number in the payload.",
          inputFields: [],
        },
      ),

      aiNode(
        "2",
        "OpenAI: Generate promotional copy for multiple channels",
        280,
        "You are a podcast marketing specialist. Given episode details, create promotional copy optimized for social media, email, and community channels. Include attention-grabbing hooks, key quotes, and calls to listen.",
        "New podcast episode: {{{1.body}}}. Return JSON with: episode_title, episode_number (string), guest_name, episode_duration, linkedin_promo (professional post with hook), twitter_promo (under 280 chars with hashtags), email_subject (catchy subject line), email_body (newsletter-style promo paragraph), key_quote (memorable quote from episode description).",
        [
          { field: "episode_title", as: "episode_title", type: "string" },
          { field: "episode_number", as: "episode_number", type: "string" },
          { field: "guest_name", as: "guest_name", type: "string" },
          { field: "episode_duration", as: "episode_duration", type: "string" },
          { field: "linkedin_promo", as: "linkedin_promo", type: "string" },
          { field: "twitter_promo", as: "twitter_promo", type: "string" },
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          { field: "key_quote", as: "key_quote", type: "string" },
        ],
      ),

      n(
        "3",
        "runCode",
        "Create social media variants with platform formatting",
        520,
        {
          codeDetails: {
            language: "javascript",
            code: `const variants = {
  linkedin: linkedin_promo,
  twitter: twitter_promo,
  instagram: "New Episode! " + episode_title + " with " + guest_name + " | Listen now (link in bio) #podcast #" + (guest_name || "").replace(/\\s+/g, "")
};
const variant_count = Object.keys(variants).length;
const instagram_caption = variants.instagram;
const all_variants_ready = true;
return { variant_count, instagram_caption, all_variants_ready };`,
            variables: [
              { name: "variant_count", type: "number" },
              { name: "instagram_caption", type: "string" },
              { name: "all_variants_ready", type: "boolean" },
            ],
          },
        },
      ),

      n("4", "approvalGate", "Content lead: Approve promotional content", 760, {
        approvalDetails: {
          approvers: ["content-lead@company.com"],
          message:
            "Podcast Promo Content for Review:\n\nEpisode #{episode_number}: {episode_title}\nGuest: {guest_name}\nDuration: {episode_duration}\n\nLinkedIn: {linkedin_promo}\nTwitter: {twitter_promo}\nInstagram: {instagram_caption}\nEmail Subject: {email_subject}\n\nPlease approve for scheduling.",
        },
      }),

      n("5", "hitApi", "Buffer: Schedule social promotion posts", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bufferapp.com/1/updates/create.json",
          headers: [
            { key: "Authorization", value: "Bearer {buffer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "profile_ids",
              value: "[{linkedin_profile_id},{twitter_profile_id}]",
            },
            { key: "text", value: "{linkedin_promo}" },
            { key: "now", value: "false" },
          ],
          variables: [
            { name: "success", customName: "buffer_success", type: "boolean" },
            { name: "updates[0].id", customName: "buffer_id", type: "string" },
          ],
        },
      }),

      emailNode(
        "6",
        "Email: Send guest a media kit with promotional assets",
        1240,
        "{guest_name}@guest-email.com",
        "Your episode is live! Here's your promo kit, {guest_name}",
        "Hi {guest_name},\n\nYour podcast episode is live!\n\nEpisode #{episode_number}: {episode_title}\nDuration: {episode_duration}\n\nHere's your promotional kit:\n\nKey Quote to Share: \"{key_quote}\"\n\nSuggested LinkedIn Post:\n{linkedin_promo}\n\nSuggested Tweet:\n{twitter_promo}\n\nFeel free to customize and share with your audience. Tag us and we'll amplify your posts!\n\nThank you for being an amazing guest.\n\nBest,\nThe Podcast Team",
      ),

      slackNode(
        "7",
        "Slack: Distribute episode assets to marketing team",
        1480,
        ':microphone: *New Podcast Episode Live!*\n*#{episode_number}: {episode_title}*\n*Guest:* {guest_name}\n*Duration:* {episode_duration}\n\n*Key Quote:* "{key_quote}"\n*Social scheduled:* {buffer_success}\n*Variants:* {variant_count} platforms\n\nPlease share on your personal channels!',
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log episode promotion activity",
        1720,
        "PodcastPromo",
        '[["{episode_number}","{episode_title}","{guest_name}","{episode_duration}","{buffer_success}","{variant_count}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automates podcast episode promotion across channels. Generates AI-optimized promotional copy for LinkedIn, Twitter, Instagram, and email, routes through content approval, schedules social posts via Buffer, sends the guest a media kit with shareable assets, and distributes episode info to the team via Slack.",
  },

  // ===================================================================
  // 67. Content Localization Request Pipeline — Pattern I
  // ===================================================================
  "Content Localization Request Pipeline": {
    nodes: [
      trigger(
        "Airtable Webhook: Localization Request Submitted",
        "webhook_app",
        {
          source: "Airtable",
          event: "record.created",
          howItStarts:
            "Fires when a new content localization request is added to the Airtable pipeline, triggering language detection, scope estimation, and regional approval routing",
          setup:
            "In Airtable, configure a webhook that fires when a new record is created in the Localization Requests table. Include source content URL, target languages, priority level, content type, and requestor email in the payload.",
          inputFields: [],
        },
      ),

      aiNode(
        "2",
        "OpenAI: Detect source language and assess content complexity",
        280,
        "You are a localization project manager. Parse the localization request and assess the source content for translation complexity, cultural adaptation needs, and estimated effort.",
        "Analyze this localization request: {{{1.body}}}. Return JSON with: source_url, source_language (detected), target_languages (comma-separated list), content_type (webpage/email/ad/document), word_count_estimate (number), complexity (simple/moderate/complex), cultural_adaptation_needed (boolean), requestor_email, priority (urgent/high/standard).",
        [
          { field: "source_url", as: "source_url", type: "string" },
          { field: "source_language", as: "source_language", type: "string" },
          { field: "target_languages", as: "target_languages", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          {
            field: "word_count_estimate",
            as: "word_count_estimate",
            type: "number",
          },
          { field: "complexity", as: "complexity", type: "string" },
          {
            field: "cultural_adaptation_needed",
            as: "cultural_adaptation_needed",
            type: "boolean",
          },
          { field: "requestor_email", as: "requestor_email", type: "string" },
          { field: "priority", as: "priority", type: "string" },
        ],
      ),

      n("3", "runCode", "Estimate localization scope and timeline", 520, {
        codeDetails: {
          language: "javascript",
          code: `const words = parseInt(word_count_estimate || "500");
const langs = (target_languages || "").split(",").length;
const complexityMultiplier = complexity === "complex" ? 2.0 : complexity === "moderate" ? 1.5 : 1.0;
const totalWords = words * langs;
const estimated_hours = Math.ceil(totalWords / 500 * complexityMultiplier);
const estimated_days = Math.ceil(estimated_hours / 8);
const is_priority_market = (target_languages || "").toLowerCase().includes("es") || (target_languages || "").toLowerCase().includes("fr") || (target_languages || "").toLowerCase().includes("de");
const estimated_cost = totalWords * 0.12;
return { totalWords, estimated_hours, estimated_days, is_priority_market, estimated_cost: estimated_cost.toFixed(2), language_count: langs };`,
          variables: [
            { name: "totalWords", type: "number" },
            { name: "estimated_hours", type: "number" },
            { name: "estimated_days", type: "number" },
            { name: "is_priority_market", type: "boolean" },
            { name: "estimated_cost", type: "string" },
            { name: "language_count", type: "number" },
          ],
        },
      }),

      n("4", "condition", "Is this for a priority market (ES, FR, DE)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_priority_market", operator: "equals", value: "true" },
          ],
        },
      }),

      aiNode(
        "5",
        "OpenAI: Translate and adapt content for target languages",
        1000,
        "You are a professional content localizer. Translate the source content while adapting cultural references, idioms, and tone for the target market. Maintain brand voice across languages.",
        "Source language: {source_language}. Target languages: {target_languages}. Content type: {content_type}. Cultural adaptation needed: {cultural_adaptation_needed}. Source URL: {source_url}. Return JSON with: translation_status (complete/partial), languages_completed (comma-separated), adaptation_notes (string describing cultural changes made), quality_score (0-100), flagged_sections (string of sections needing human review).",
        [
          {
            field: "translation_status",
            as: "translation_status",
            type: "string",
          },
          {
            field: "languages_completed",
            as: "languages_completed",
            type: "string",
          },
          { field: "adaptation_notes", as: "adaptation_notes", type: "string" },
          { field: "quality_score", as: "quality_score", type: "number" },
          { field: "flagged_sections", as: "flagged_sections", type: "string" },
        ],
      ),

      n("6", "approvalGate", "Regional lead: Review localized content", 1240, {
        approvalDetails: {
          approvers: ["regional-lead@company.com"],
          message:
            "Localization Review Required:\n\nSource: {source_language} → {target_languages}\nContent Type: {content_type}\nComplexity: {complexity}\nQuality Score: {quality_score}/100\nAdaptation Notes: {adaptation_notes}\nFlagged Sections: {flagged_sections}\nEstimated Cost: ${estimated_cost}\n\nPlease review and approve the localized content.",
        },
      }),

      slackNode(
        "7",
        "Slack: Notify localization team of pipeline update",
        1480,
        ":globe_with_meridians: *Localization Pipeline Update*\n*Source:* {source_language} → {target_languages}\n*Type:* {content_type} | *Priority:* {priority}\n*Scope:* {totalWords} words across {language_count} languages\n*Timeline:* ~{estimated_days} days | *Cost:* ${estimated_cost}\n*Status:* {translation_status}\n*Quality:* {quality_score}/100",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log localization request and status",
        1720,
        "LocalizationPipeline",
        '[["{source_language}","{target_languages}","{content_type}","{complexity}","{totalWords}","{estimated_days}","{estimated_cost}","{translation_status}","{quality_score}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Manages the content localization pipeline from request to regional approval. Detects source language, estimates scope and cost, performs AI translation with cultural adaptation, routes through regional lead review, notifies the localization team via Slack, and tracks all requests in Google Sheets.",
  },

  // ===================================================================
  // 68. Influencer Outreach Tracker — Pattern J
  // ===================================================================
  "Influencer Outreach Tracker": {
    nodes: [
      trigger(
        "Airtable Webhook: New Influencer Added to Pipeline",
        "webhook_app",
        {
          source: "Airtable",
          event: "record.created",
          howItStarts:
            "Fires when a new influencer is added to the outreach pipeline in Airtable, triggering background research, fit scoring, and personalized pitch generation",
          setup:
            "In Airtable, configure a webhook that fires when a new record is created in the Influencer Pipeline table. Include influencer name, handle, platform, follower count, niche, and any notes in the payload.",
          inputFields: [],
        },
      ),

      aiNode(
        "2",
        "OpenAI: Research influencer background and audience fit",
        280,
        "You are an influencer marketing strategist. Analyze the influencer profile to assess brand fit, audience alignment, engagement quality, and collaboration potential. Consider their niche, content style, and audience demographics.",
        "Research this influencer: {{{1.body}}}. Return JSON with: influencer_name, influencer_handle, platform (instagram/tiktok/youtube/linkedin), follower_count (number), niche, content_style (educational/entertaining/lifestyle/technical), audience_alignment (high/medium/low), engagement_estimate (string like '3-5%'), collaboration_type (sponsored_post/ambassador/affiliate/content_series), brand_fit_notes (string with specific reasons).",
        [
          { field: "influencer_name", as: "influencer_name", type: "string" },
          {
            field: "influencer_handle",
            as: "influencer_handle",
            type: "string",
          },
          { field: "platform", as: "platform", type: "string" },
          { field: "follower_count", as: "follower_count", type: "number" },
          { field: "niche", as: "niche", type: "string" },
          {
            field: "audience_alignment",
            as: "audience_alignment",
            type: "string",
          },
          {
            field: "collaboration_type",
            as: "collaboration_type",
            type: "string",
          },
          { field: "brand_fit_notes", as: "brand_fit_notes", type: "string" },
        ],
      ),

      n("3", "runCode", "Score influencer fit for brand partnership", 520, {
        codeDetails: {
          language: "javascript",
          code: `const followers = parseInt(follower_count || "0");
const alignmentScore = audience_alignment === "high" ? 40 : audience_alignment === "medium" ? 25 : 10;
const sizeScore = followers > 100000 ? 30 : followers > 10000 ? 20 : 10;
const fit_score = alignmentScore + sizeScore;
const passes_threshold = fit_score > 55;
const tier = followers > 500000 ? "mega" : followers > 100000 ? "macro" : followers > 10000 ? "micro" : "nano";
const budget_estimate = tier === "mega" ? "$5,000+" : tier === "macro" ? "$1,000-5,000" : tier === "micro" ? "$200-1,000" : "$50-200";
return { fit_score, passes_threshold, tier, budget_estimate };`,
          variables: [
            { name: "fit_score", type: "number" },
            { name: "passes_threshold", type: "boolean" },
            { name: "tier", type: "string" },
            { name: "budget_estimate", type: "string" },
          ],
        },
      }),

      n(
        "4",
        "condition",
        "Does the influencer meet the fit score threshold (>55)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "passes_threshold", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      emailNode(
        "5",
        "Email: Send personalized outreach pitch to influencer",
        1000,
        "{influencer_handle}@{platform}-outreach.com",
        "Collaboration opportunity for @{influencer_handle} — let's create something amazing",
        "Hi {influencer_name},\n\nI've been following your content on {platform} and love what you're doing in the {niche} space. Your {collaboration_type} content style is exactly the kind of authentic voice we value.\n\nWe'd love to explore a {collaboration_type} partnership. Here's why we think this could be a great fit:\n\n{brand_fit_notes}\n\nWhat we have in mind:\n- A {collaboration_type} collaboration that aligns with your content\n- Creative freedom to make it authentically yours\n- Fair compensation based on your audience reach\n\nWould you be open to a quick 15-minute chat to explore this?\n\nBest,\nInfluencer Partnerships Team",
      ),

      n("6", "delayAction", "Wait 5 days for influencer response", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      n("7", "condition", "Has the influencer responded to the pitch?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "fit_score", operator: "greaterThan", value: "70" },
          ],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Update influencer outreach status",
        1720,
        "InfluencerOutreach",
        '[["{influencer_name}","{influencer_handle}","{platform}","{follower_count}","{tier}","{fit_score}","{collaboration_type}","{budget_estimate}","{passes_threshold}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Manages influencer outreach from discovery to response tracking. AI researches influencer background and audience fit, scores partnership potential, sends personalized pitch emails to qualifying influencers, waits for responses, and tracks all outreach activity in Google Sheets with tier and budget estimates.",
  },

  // ===================================================================
  // 69. Attribution Dashboard Updater — Pattern K
  // ===================================================================
  "Attribution Dashboard Updater": {
    nodes: [
      trigger("Schedule: Daily Attribution Data Refresh", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Runs daily at 6:00 AM to pull pipeline data from HubSpot and conversion data from Google Analytics, normalize attribution models, and update the Google Sheets dashboard",
        setup:
          "Configure HubSpot API for deal pipeline access and Google Analytics Data API for conversion data. Set the GA4 property ID and HubSpot pipeline ID. Schedule fires daily at 6AM.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "HubSpot: Fetch pipeline data with attribution sources",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/deals/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"createdate","operator":"GTE","value":"{thirty_days_ago_epoch}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["dealname","amount","dealstage","hs_analytics_source","pipeline","createdate"]',
              },
              { key: "limit", value: "100" },
            ],
            variables: [
              { name: "total", customName: "total_deals", type: "number" },
              { name: "results", customName: "deal_data", type: "string" },
            ],
          },
        },
      ),

      n(
        "3",
        "hitApi",
        "Google Analytics: Fetch conversion data by channel",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "dateRanges",
                value: '[{"startDate":"30daysAgo","endDate":"today"}]',
              },
              {
                key: "metrics",
                value:
                  '[{"name":"conversions"},{"name":"totalRevenue"},{"name":"sessions"}]',
              },
              {
                key: "dimensions",
                value:
                  '[{"name":"sessionDefaultChannelGroup"},{"name":"sessionSource"}]',
              },
            ],
            variables: [
              {
                name: "rows",
                customName: "ga_conversion_data",
                type: "string",
              },
              { name: "rowCount", customName: "ga_row_count", type: "number" },
            ],
          },
        },
      ),

      n(
        "4",
        "runCode",
        "Normalize attribution data across CRM and analytics",
        760,
        {
          codeDetails: {
            language: "javascript",
            code: `const deals = JSON.parse(deal_data || "[]");
const sourceMap = {};
deals.forEach(d => {
  const src = (d.properties?.hs_analytics_source || "unknown").toLowerCase();
  if (!sourceMap[src]) sourceMap[src] = { deals: 0, revenue: 0 };
  sourceMap[src].deals++;
  sourceMap[src].revenue += parseFloat(d.properties?.amount || 0);
});
const attribution_breakdown = Object.entries(sourceMap).map(([src, data]) => src + ": " + data.deals + " deals, $" + data.revenue.toFixed(0)).join(" | ");
const total_pipeline_revenue = Object.values(sourceMap).reduce((sum, d) => sum + d.revenue, 0);
const source_count = Object.keys(sourceMap).length;
const has_attribution_gap = sourceMap["unknown"]?.deals > 0;
return { attribution_breakdown, total_pipeline_revenue: total_pipeline_revenue.toFixed(0), source_count, has_attribution_gap };`,
            variables: [
              { name: "attribution_breakdown", type: "string" },
              { name: "total_pipeline_revenue", type: "string" },
              { name: "source_count", type: "number" },
              { name: "has_attribution_gap", type: "boolean" },
            ],
          },
        },
      ),

      sheetsNode(
        "5",
        "Google Sheets: Update attribution dashboard with daily data",
        1000,
        "AttributionDashboard",
        '[["{total_deals}","{total_pipeline_revenue}","{source_count}","{attribution_breakdown}","{has_attribution_gap}","{ga_row_count}","=NOW()"]]',
      ),

      n(
        "6",
        "condition",
        "Are there attribution gaps (unknown sources)?",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                field: "has_attribution_gap",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),

      slackNode(
        "7",
        "Slack: Alert ops team of attribution gaps",
        1480,
        ":chart_with_upwards_trend: *Daily Attribution Dashboard Updated*\n*Pipeline Deals:* {total_deals}\n*Pipeline Revenue:* ${total_pipeline_revenue}\n*Sources:* {source_count}\n*Gaps:* {has_attribution_gap}\n\n*Breakdown:* {attribution_breakdown}\n\n:warning: Review unknown-source deals and backfill attribution.",
      ),

      emailNode(
        "8",
        "Email: Weekly attribution summary to marketing ops",
        1720,
        "marketing-ops@company.com",
        "Attribution Dashboard Update — ${total_pipeline_revenue} pipeline, {source_count} sources",
        "Daily Attribution Dashboard Report\n\nPipeline Overview:\n- Total Deals (30 days): {total_deals}\n- Pipeline Revenue: ${total_pipeline_revenue}\n- Attribution Sources: {source_count}\n- Gaps Detected: {has_attribution_gap}\n\nSource Breakdown:\n{attribution_breakdown}\n\nGA Channels Tracked: {ga_row_count}\n\nThe Google Sheets dashboard has been updated with today's data. Please review any attribution gaps and ensure UTMs are properly configured.\n\nAttribution Bot",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Refreshes the marketing attribution dashboard daily by pulling HubSpot pipeline data and Google Analytics conversions. Normalizes attribution across sources, updates Google Sheets with daily metrics, flags attribution gaps, alerts the ops team via Slack, and sends weekly summaries to marketing ops.",
  },

  // ===================================================================
  // 70. Regional Campaign Asset Approval — Pattern I
  // ===================================================================
  "Regional Campaign Asset Approval": {
    nodes: [
      trigger(
        "Airtable Webhook: Regional Campaign Asset Uploaded",
        "webhook_app",
        {
          source: "Airtable",
          event: "record.created",
          howItStarts:
            "Fires when a new campaign asset is uploaded to the regional assets table in Airtable, triggering brand compliance checks and regional approval routing",
          setup:
            "In Airtable, configure a webhook that fires on new record creation in the Regional Assets table. Include asset URL, asset type (image/video/copy/banner), target region, language, campaign name, and uploader email in the payload.",
          inputFields: [],
        },
      ),

      aiNode(
        "2",
        "OpenAI: Check asset for brand compliance",
        280,
        "You are a brand compliance specialist. Analyze the uploaded campaign asset for adherence to brand guidelines including logo usage, color palette, typography, tone of voice, and legal disclaimers. Flag any violations.",
        "Check brand compliance for: {{{1.body}}}. Return JSON with: asset_url, asset_type (image/video/copy/banner), campaign_name, uploader_email, brand_compliant (boolean), compliance_issues (comma-separated list or 'none'), logo_correct (boolean), colors_correct (boolean), tone_appropriate (boolean), compliance_score (0-100).",
        [
          { field: "asset_url", as: "asset_url", type: "string" },
          { field: "asset_type", as: "asset_type", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "uploader_email", as: "uploader_email", type: "string" },
          { field: "brand_compliant", as: "brand_compliant", type: "boolean" },
          {
            field: "compliance_issues",
            as: "compliance_issues",
            type: "string",
          },
          { field: "compliance_score", as: "compliance_score", type: "number" },
        ],
      ),

      n(
        "3",
        "runCode",
        "Identify target region and language requirements",
        520,
        {
          codeDetails: {
            language: "javascript",
            code: `const body = JSON.parse(trigger_body || "{}");
const target_region = body.fields?.region || body.region || "global";
const target_language = body.fields?.language || body.language || "en";
const needs_legal_review = ["de","fr","jp","br"].includes(target_language.toLowerCase()) || target_region.toLowerCase().includes("eu");
const regional_lead_email = {
  "na": "na-lead@company.com",
  "emea": "emea-lead@company.com",
  "apac": "apac-lead@company.com",
  "latam": "latam-lead@company.com"
}[target_region.toLowerCase()] || "global-lead@company.com";
return { target_region, target_language, needs_legal_review, regional_lead_email };`,
            variables: [
              { name: "target_region", type: "string" },
              { name: "target_language", type: "string" },
              { name: "needs_legal_review", type: "boolean" },
              { name: "regional_lead_email", type: "string" },
            ],
          },
        },
      ),

      n(
        "4",
        "condition",
        "Does this asset need legal review for the target region?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "needs_legal_review",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),

      n(
        "5",
        "approvalGate",
        "Regional manager: Review and approve campaign asset",
        1000,
        {
          approvalDetails: {
            approvers: ["{regional_lead_email}"],
            message:
              "Regional Campaign Asset Approval:\n\nCampaign: {campaign_name}\nAsset Type: {asset_type}\nRegion: {target_region} ({target_language})\nAsset: {asset_url}\n\nBrand Compliance: {compliance_score}/100\nIssues: {compliance_issues}\nLegal Review Needed: {needs_legal_review}\n\nUploaded by: {uploader_email}\n\nPlease review and approve for regional deployment.",
          },
        },
      ),

      n(
        "6",
        "hitApi",
        "Airtable: Update asset record with approval status",
        1240,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.airtable.com/v0/{base_id}/Regional%20Assets/{record_id}",
            headers: [
              { key: "Authorization", value: "Bearer {airtable_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"Status":"Approved","Compliance Score":"{compliance_score}","Region":"{target_region}","Language":"{target_language}","Reviewed By":"{regional_lead_email}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_record_id", type: "string" },
              {
                name: "fields.Status",
                customName: "asset_status",
                type: "string",
              },
            ],
          },
        },
      ),

      slackNode(
        "7",
        "Slack: Notify marketing team of asset approval decision",
        1480,
        ":white_check_mark: *Regional Asset Approved*\n*Campaign:* {campaign_name}\n*Type:* {asset_type}\n*Region:* {target_region} ({target_language})\n*Compliance:* {compliance_score}/100\n*Legal Review:* {needs_legal_review}\n*Status:* {asset_status}\n*Asset:* {asset_url}",
      ),

      emailNode(
        "8",
        "Email: Confirm asset approval to uploader",
        1720,
        "{uploader_email}",
        "Your campaign asset for {target_region} has been approved — {campaign_name}",
        "Hi,\n\nYour campaign asset has been reviewed and approved for regional deployment.\n\nCampaign: {campaign_name}\nAsset Type: {asset_type}\nRegion: {target_region}\nLanguage: {target_language}\nCompliance Score: {compliance_score}/100\n\nCompliance Notes: {compliance_issues}\nLegal Review: {needs_legal_review}\n\nThe asset is now cleared for deployment in the {target_region} region. Please proceed with campaign activation.\n\nRegional Ops Bot",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Manages regional campaign asset approval workflows. AI checks brand compliance, identifies the target region and language requirements, flags assets needing legal review, routes through regional manager approval, updates Airtable status, notifies the team via Slack, and confirms approval to the asset uploader.",
  },
};
